import { useCallback, useEffect, useState } from 'react';
import { Content, Flex, Link, ProgressCircle, View } from '@adobe/react-spectrum';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { ListInvoiceRequest } from '../../../../services/soap/invoice/requests/ListInvoiceRequest';
import InvoiceListTable from '../../../../pages/protected/invoice/InvoiceListTable';
import { ILayoutComponentProps, LayoutComponent } from '../LayoutComponent';
import { useInvoiceWrapperApp } from './invoiceWrapper.hooks';
import { invokeInvoiceApp } from '../../../../utils/NavigationUtils';
import { InvoiceType, mapInvoiceTypeToAppIdent } from '../../../../shared/invoice/invoiceConfig';
import styles from './InvoiceWrapper.module.css';
import { statusColor } from '../../../../utils/helperUtil';
import { FPADataTypes } from '../../../../infra/protected/FPA/FPAData';

export interface IInvoiceWrapperProps extends ILayoutComponentProps {
}

function InvoiceWrapper({ selectedItem }: IInvoiceWrapperProps) {
  const { t } = useTranslation(['layout_components', 'invoice']);
  const { store, invoiceService } = useDependency();
  const { apiParams, invoiceType } = useInvoiceWrapperApp((selectedItem as any)?.invoiceViewComponent);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rowsData, setRowsData] = useState<any[]>([]);

  const getStableColorSeedFromState = (state: any) => {
    const key = String(state ?? '');
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    }
    return (hash % 16777215) + 1;
  };

  const getStateColor = (state: any) => {
    const colorSeed = getStableColorSeedFromState(state);
    return statusColor(colorSeed.toString());
  };

  const getContextFilter = useCallback(() => {
    const item: any = selectedItem || {};
    const type = item?.type;
    const id = item?.id != null ? String(item.id) : '';
    const parentId = item?.parent_id != null ? String(item.parent_id) : '';
    const parentParentId =
      item?.parent?.parent_id != null
        ? String(item.parent.parent_id)
        : (item?.extra?.folderId != null ? String(item.extra.folderId) : '');

    const contextFilter = (() => {
      switch (type) {
      case FPADataTypes.ACTIVITY:
        return {
          ...(parentParentId ? { folder: parentParentId } : {}),
          ...(parentId ? { project: parentId } : {}),
          ...(id ? { activity: id } : {}),
        };
      case FPADataTypes.PROJECT:
        return {
          ...(parentId ? { folder: parentId } : {}),
          ...(id ? { project: id } : {}),
        };
      case FPADataTypes.FOLDER:
        return {
          ...(id ? { folder: id } : {}),
        };
      default:
        return {};
      }
    })();

    console.log('[InvoiceWrapper] context filter resolved', item);

    return contextFilter;
  }, [selectedItem]);

  const loadInvoices = useCallback(async () => {
    setIsLoading(true);
    try {
      const contextFilter = getContextFilter();
      const response: any = await invoiceService.listInvoices(
        new ListInvoiceRequest(store.Server, store.SessionId, {
          FILTER: { ...apiParams, ...contextFilter },
          limit: 100,
          offset: 0,
          orderBy: 'created',
          orderByType: '1',
        })
      );

      if (response?.result !== 'OK') {
        setRowsData([]);
        return;
      }

      const invoices = Array.isArray(response?.INVOICE)
        ? response.INVOICE
        : response?.INVOICE
          ? [response.INVOICE]
          : [];

      const mapped = invoices.map((item: any) => ({
        ...item,
        status: item?.statusName || item?.stateName || item?.state || '',
        statusColor: getStateColor(item?.state),
        currency: item?.currency || item?.currencyName || '',
        total: item?.total ?? item?.totalWithVAT ?? 0,
      }));

      setRowsData(mapped);
    } catch (error) {
      console.error('Failed to load invoice list in basic layout:', error);
      setRowsData([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiParams, getContextFilter, invoiceService, store.Server, store.SessionId]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const headingText = (() => {
      switch (invoiceType) {
      case InvoiceType.ORDER_RECEIVED:
        return t("wrapper_sales_orders", { ns: 'invoice', defaultValue: "Sales Orders" });
      case InvoiceType.ORDER_ISSUED:
        return t("wrapper_purchase_orders", { ns: 'invoice', defaultValue: "Purchase Orders" });
      case InvoiceType.INVOICE_RECEIVED:
        return t("wrapper_received_invoices", { ns: 'invoice', defaultValue: "Received Invoices" });
      case InvoiceType.INVOICE_ISSUED:
        return t("wrapper_issued_invoices", { ns: 'invoice', defaultValue: "Issued Invoices" });
      case InvoiceType.OFFER_RECEIVED:
        return t("wrapper_received_offers", { ns: 'invoice', defaultValue: "Received Offers" });
      case InvoiceType.OFFER_ISSUED:
        return t("wrapper_estimates", { ns: 'invoice', defaultValue: "Estimates" });
      default: {
        const headingTextRaw = (apiParams?.filterTitle || "Invoices")
          .replace(/^all\s+/i, "")
          .trim();
        return headingTextRaw
          ? headingTextRaw.charAt(0).toUpperCase() + headingTextRaw.slice(1)
          : t("wrapper_invoices", { ns: 'invoice', defaultValue: "Invoices" });
      }
    }
  })();
  const invoiceAppIdent = mapInvoiceTypeToAppIdent(invoiceType);
  const addButtonLabel = (() => {
    switch (invoiceType) {
      case InvoiceType.ORDER_RECEIVED:
        return t("wrapper_create_sales_order", { ns: 'invoice', defaultValue: "Create Sales Order" });
      case InvoiceType.ORDER_ISSUED:
        return t("wrapper_create_purchase_order", { ns: 'invoice', defaultValue: "Create Purchase Order" });
      case InvoiceType.INVOICE_RECEIVED:
        return t("wrapper_add_received_invoice", { ns: 'invoice', defaultValue: "Add Received Invoice" });
      case InvoiceType.INVOICE_ISSUED:
        return t("wrapper_add_issued_invoice", { ns: 'invoice', defaultValue: "Add Issued Invoice" });
      case InvoiceType.OFFER_RECEIVED:
        return t("wrapper_add_received_offer", { ns: 'invoice', defaultValue: "Add Received Offer" });
      case InvoiceType.OFFER_ISSUED:
        return t("wrapper_add_estimate", { ns: 'invoice', defaultValue: "Add Estimate" });
      default:
        return t("wrapper_add_invoice", { ns: 'invoice', defaultValue: "Add Invoice" });
    }
  })();

  const handleOpenInvoiceApp = (item: any) => {
    if (!item?.id) return;
    invokeInvoiceApp(String(item.id), invoiceAppIdent);
  };

  const handleOpenAddInvoiceApp = () => {
    const contextFilter = getContextFilter();
    console.log('[InvoiceWrapper] open add invoice', {
      invoiceAppIdent,
      mode: 'add',
      contextFilter,
    });
    invokeInvoiceApp(undefined, invoiceAppIdent, 'add', contextFilter);
  };

  return (
    <Flex direction={'column'} width={'100%'}>
      <Flex direction={'row'} alignItems={'center'} justifyContent={'start'} UNSAFE_className={styles.header_row}>
        <Content position={'relative'} UNSAFE_className={styles.heading_text}>
          {headingText}
        </Content>
        <Flex direction={'row'} alignItems={'center'} justifyContent={'center'} gap={'size-100'} UNSAFE_className={styles.icon_add_parent}>
          <Content>
            <Link isQuiet onPress={handleOpenAddInvoiceApp}>
              <i className="bi bi-plus fs-5">
                <View UNSAFE_className={styles.icon_add_text}>{addButtonLabel}</View>
              </i>
            </Link>
          </Content>
        </Flex>
      </Flex>
      {isLoading ? (
        <Flex width="100%" justifyContent={'center'} marginTop={10}>
          <ProgressCircle aria-label={t('loading', { ns: 'layout_components', defaultValue: 'Loading...' })} isIndeterminate />
        </Flex>
      ) : rowsData.length > 0 ? (
        <View UNSAFE_className={styles.table_wrapper}>
          <InvoiceListTable
            invoiceList={rowsData}
            handleTableRowclick={() => { }}
            showAddButton={false}
            handleAddNew={handleOpenAddInvoiceApp}
            handleEditClick={handleOpenInvoiceApp}
          />
        </View>
      ) : (
        <View>{t('no_results', { ns: 'layout_components', defaultValue: 'No results' })}</View>
      )}
    </Flex>
  );
}

export const InvoiceWrapperComponent = LayoutComponent(InvoiceWrapper);
